"use client";

import { useTranslations } from "next-intl";
import { Image } from "@/lib/client/components/Image";
import { AspectRatio, Section, Text } from "@/ui";

interface ImageData {
  src: string;
  alt: string;
}

interface ImageGroup {
  className: string;
  images: ImageData[];
}

const STORAGE_URL = process.env.NEXT_PUBLIC_R2_PUBLIC_URL || "";

const imageGroups: ImageGroup[] = [
  {
    className:
      "ml-auto w-44 flex-none space-y-8 pt-32 sm:ml-0 sm:pt-80 lg:order-last lg:pt-36 xl:order-none xl:pt-80",
    images: [
      {
        src: `${STORAGE_URL}/images/AboutUs/1.jpg`,
        alt: "Team discussing philosophical concepts",
      },
    ],
  },
  {
    className: "mr-auto w-44 flex-none space-y-8 pt-20 sm:mr-0 sm:pt-52 lg:pt-36",
    images: [
      {
        src: `${STORAGE_URL}/images/AboutUs/2.jpg`,
        alt: "Philosopher presenting ideas",
      },
      {
        src: `${STORAGE_URL}/images/AboutUs/6.jpg`,
        alt: "Group meditation session",
      },
    ],
  },
  {
    className: "w-44 flex-none space-y-8",
    images: [
      {
        src: `${STORAGE_URL}/images/AboutUs/4.jpg`,
        alt: "Inspirational lecture hall",
      },
      {
        src: `${STORAGE_URL}/images/AboutUs/5.jpg`,
        alt: "Researchers in discussion",
      },
    ],
  },
  {
    className: "mr-auto w-44 flex-none space-y-8 pt-20 sm:mr-0 sm:pt-52 lg:pt-36",
    images: [
      {
        src: `${STORAGE_URL}/images/AboutUs/7.jpg`,
        alt: "Interactive seminar",
      },
      {
        src: `${STORAGE_URL}/images/AboutUs/8.jpg`,
        alt: "Philosophical debate session",
      },
    ],
  },
];

const ImageCard = ({
  src,
  alt,
  width = 250,
  height = 250,
  quality = 75,
  className = "",
}: {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  quality?: number;
  className?: string;
}) => (
  <AspectRatio
    ratio={1}
    className={`rounded-2xl shadow-2xl ${className}`}
    asChild
  >
    <Image
      src={src}
      width={width}
      height={height}
      quality={quality}
      alt={alt}
      className="absolute inset-0 h-full w-full object-cover"
    />
  </AspectRatio>
);

export const PhilosophySection = () => {
  const philosophyT = useTranslations("philosophy");

  return (
    <Section
      id="philosophy"
      className="relative isolate px-0"
    >
      <div className="overflow-hidden">
        <div className="mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-center">
          {/* Text Content */}
          <div className="w-full max-w-xl lg:shrink-0 xl:max-w-2xl">
            <Text className="text-4xl font-bold tracking-tight sm:text-6xl">
              {philosophyT("title")}
            </Text>
            <Text className="mt-6 text-lg leading-8 text-primary sm:max-w-md lg:max-w-none">
              {philosophyT("subtitle")}
            </Text>
            <Text className="text-lg leading-8 sm:max-w-md lg:max-w-none">
              {philosophyT("description")}
            </Text>
          </div>

          {/* Image Gallery */}
          <div className="mt-14 flex justify-end gap-8 sm:-mt-44 sm:justify-start sm:pl-20 lg:mt-0 lg:pl-0">
            {imageGroups.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className={group.className}
              >
                {group.images.map((image, imageIndex) => (
                  <ImageCard
                    alt={image.alt}
                    src={image.src}
                    key={imageIndex}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};
